import { useState } from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { Home, Plus, Check, Loader2, Trash2, Lock, Share2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import GlassCard from "./GlassCard";
import { SuburbSelect } from "./SuburbSelect";
import UpgradePlanSheet from "./UpgradePlanSheet";
import SharePropertySheet from "./SharePropertySheet";

const PropertySwitcher = ({ open, onOpenChange }) => {
    const { user } = useAuth();
    const propertyContext = usePropertyContext();
    const { canAddProperty, plan, isPropertyLocked, isFeatureLocked } = useSubscription();

    const { properties, activeProperty, setActiveProperty, addProperty, updateProperty, deleteProperty, loading } = propertyContext;

    const [showAddForm, setShowAddForm] = useState(false);
    const [editingPropertyId, setEditingPropertyId] = useState(null);
    const [showUpgradeSheet, setShowUpgradeSheet] = useState(false);
    const [propertyToShare, setPropertyToShare] = useState(null);
    const [newPropertyName, setNewPropertyName] = useState("");
    const [newPropertySuburb, setNewPropertySuburb] = useState("");
    const [saving, setSaving] = useState(false);
    const [switching, setSwitching] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(null);

    const handleOpenAddForm = () => {
        if (!canAddProperty) {
            setShowUpgradeSheet(true);
            return;
        }
        setEditingPropertyId(null);
        setNewPropertyName("");
        setNewPropertySuburb("");
        setShowAddForm(true);
    };

    const handleOpenEditForm = (property) => {
        setEditingPropertyId(property.id);
        setNewPropertyName(property.name);
        setNewPropertySuburb(property.suburb || "");
        setShowAddForm(true);
    };

    const handleSwitchProperty = async (property) => {
        if (property.id === activeProperty?.id) return;

        setSwitching(property.id);
        await setActiveProperty(property.id);
        setSwitching(null);
        onOpenChange(false);
    };

    const handleSaveProperty = async () => {
        if (!newPropertyName.trim() || !newPropertySuburb) return;

        setSaving(true);
        let result;
        if (editingPropertyId) {
            result = await updateProperty(editingPropertyId, {
                name: newPropertyName.trim(),
                suburb: newPropertySuburb,
            });
        } else {
            result = await addProperty({
                name: newPropertyName.trim(),
                suburb: newPropertySuburb,
            });
        }
        setSaving(false);

        if (result) {
            setNewPropertyName("");
            setNewPropertySuburb("");
            setEditingPropertyId(null);
            setShowAddForm(false);
        }
    };

    const handleDeleteProperty = async () => {
        if (!deleteDialog) return;

        const success = await deleteProperty(deleteDialog.id);
        if (success) {
            setDeleteDialog(null);
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent side="bottom" className="max-h-[85vh] rounded-t-3xl overflow-y-auto">
                    <SheetHeader className="pb-4">
                        <SheetTitle className="text-xl font-bold text-foreground">
                            Switch Property
                        </SheetTitle>
                        <SheetDescription>
                            Select a property or add a new one
                        </SheetDescription>
                    </SheetHeader>

                    <div className="space-y-4">
                        {loading ? (
                            <div className="flex justify-center py-8">
                                <Loader2 className="w-6 h-6 text-primary animate-spin" />
                            </div>
                        ) : (
                            <>
                                {/* Property List */}
                                <div className="space-y-3 max-h-[50vh] overflow-y-auto px-1.5 -mx-1.5 py-1 -my-1 scrollbar-thin">
                                    {(properties || []).map((property) => {
                                        const isOwner = property.user_id === user?.id;
                                        const isLocked = isPropertyLocked(property.id);

                                        return (
                                            <div key={property.id} className="group relative">
                                                <div
                                                    onClick={() => {
                                                        handleSwitchProperty(property);
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === 'Space') {
                                                            e.preventDefault();
                                                            handleSwitchProperty(property);
                                                        }
                                                    }}
                                                    className={cn("w-full text-left cursor-pointer", switching !== null && "pointer-events-none")}
                                                >
                                                    <GlassCard
                                                        className={cn(
                                                            "p-4 transition-all relative overflow-hidden",
                                                            property.id === activeProperty?.id
                                                                ? "ring-2 ring-primary bg-primary/5"
                                                                : "hover:bg-accent/50",
                                                            isLocked && "opacity-75 grayscale"
                                                        )}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className={cn(
                                                                "p-2 rounded-xl self-start mt-0.5 relative",
                                                                isLocked ? "bg-muted" : "bg-primary/10"
                                                            )}>
                                                                {isLocked ? (
                                                                    <div className="relative">
                                                                        <Home className="w-5 h-5 text-muted-foreground" />
                                                                        <div className="absolute -top-2 -right-2 bg-amber-500 rounded-full p-0.5 border-2 border-white">
                                                                            <Lock className="w-3 h-3 text-white" />
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <Home className="w-5 h-5 text-primary" />
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0 pr-1">
                                                                <div className="flex items-center gap-2 mb-0.5">
                                                                    <h3 className="font-medium text-foreground truncate">{property.name}</h3>
                                                                    {!isOwner && (
                                                                        <span className="px-1.5 py-0.5 rounded-full bg-muted text-[10px] font-medium text-muted-foreground uppercase tracking-wider shrink-0">
                                                                            Shared
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {property.suburb && (
                                                                    <p className="text-sm text-muted-foreground truncate">{property.suburb}</p>
                                                                )}

                                                                {/* Mobile-Friendly Action Buttons */}
                                                                <div className="flex items-center gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                                                                    {isOwner && !isLocked && (
                                                                        <>
                                                                            <Button
                                                                                variant="outline"
                                                                                size="sm"
                                                                                className="h-7 px-2 text-xs gap-1.5"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleOpenEditForm(property);
                                                                                }}
                                                                            >
                                                                                <Pencil className="w-3.5 h-3.5" />
                                                                                Edit
                                                                            </Button>
                                                                            <Button
                                                                                variant="outline"
                                                                                size="sm"
                                                                                className={cn(
                                                                                    "h-7 px-2 text-xs gap-1.5",
                                                                                    isFeatureLocked('sharing') && "border-amber-500/30 text-amber-700 bg-amber-50/5 hover:bg-amber-50/10"
                                                                                )}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    if (isFeatureLocked('sharing')) {
                                                                                        setShowUpgradeSheet(true);
                                                                                    } else {
                                                                                        setPropertyToShare(property);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {isFeatureLocked('sharing') ? (
                                                                                    <Lock className="w-3.5 h-3.5 text-amber-500" />
                                                                                ) : (
                                                                                    <Share2 className="w-3.5 h-3.5" />
                                                                                )}
                                                                                Share
                                                                            </Button>
                                                                            {properties?.length > 1 && (
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setDeleteDialog({
                                                                                            id: property.id,
                                                                                            name: property.name,
                                                                                        });
                                                                                    }}
                                                                                >
                                                                                    <Trash2 className="w-4 h-4" />
                                                                                </Button>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {isLocked && (
                                                                        <span className="text-xs font-medium text-amber-600 flex items-center gap-1">
                                                                            <Lock className="w-3 h-3" /> Upgrade to access
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Selection Indicator */}
                                                            <div className="self-center pl-2">
                                                                {switching === property.id ? (
                                                                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                                                                ) : property.id === activeProperty?.id ? (
                                                                    <div className="p-1 bg-primary rounded-full shadow-sm">
                                                                        <Check className="w-3 h-3 text-primary-foreground" />
                                                                    </div>
                                                                ) : <div className="w-5 h-5" />}
                                                            </div>
                                                        </div>

                                                        {/* Locked Overlay */}
                                                        {isLocked && (
                                                            <div className="absolute inset-0 z-10 bg-white/10 backdrop-blur-[1px]" />
                                                        )}
                                                    </GlassCard>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Add New Property */}
                                {!showAddForm ? (
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "w-full gap-2",
                                            !canAddProperty && "border-amber-500/50 text-amber-600 hover:bg-amber-50"
                                        )}
                                        onClick={handleOpenAddForm}
                                    >
                                        {!canAddProperty ? (
                                            <>
                                                <Lock className="w-4 h-4" />
                                                Upgrade to Add Property
                                            </>
                                        ) : (
                                            <>
                                                <Plus className="w-4 h-4" />
                                                Add New Property
                                            </>
                                        )}
                                    </Button>
                                ) : (
                                    <GlassCard className="p-4 space-y-4">
                                        <h3 className="font-bold text-sm text-foreground">
                                            {editingPropertyId ? "Edit Property" : "Add New Property"}
                                        </h3>
                                        <div className="space-y-2">
                                            <Label htmlFor="propertyName">Home Nickname *</Label>
                                            <Input
                                                id="propertyName"
                                                value={newPropertyName}
                                                onChange={(e) => setNewPropertyName(e.target.value)}
                                                placeholder="e.g., Primary Residence"
                                                className="rounded-xl"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="propertySuburb">Suburb / Area *</Label>
                                            <SuburbSelect
                                                value={newPropertySuburb}
                                                onSelect={(val) => setNewPropertySuburb(val)}
                                                placeholder="Search your area..."
                                                className="rounded-xl w-full"
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                className="flex-1"
                                                onClick={() => {
                                                    setShowAddForm(false);
                                                    setNewPropertyName("");
                                                    setNewPropertySuburb("");
                                                    setEditingPropertyId(null);
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                className="flex-1"
                                                onClick={handleSaveProperty}
                                                disabled={!newPropertyName.trim() || !newPropertySuburb || saving}
                                            >
                                                {saving ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    editingPropertyId ? "Save" : "Add"
                                                )}
                                            </Button>
                                        </div>
                                    </GlassCard>
                                )}
                            </>
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!deleteDialog} onOpenChange={(open) => !open && setDeleteDialog(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Property?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{deleteDialog?.name}"?
                            <span className="block mt-2 text-destructive font-medium">
                                This will permanently delete all associated data including appliances, paint codes, and service logs.
                            </span>
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteProperty}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <UpgradePlanSheet
                open={showUpgradeSheet}
                onOpenChange={setShowUpgradeSheet}
                currentPlan={plan}
            />

            <SharePropertySheet
                open={!!propertyToShare}
                onOpenChange={(open) => !open && setPropertyToShare(null)}
                property={propertyToShare}
            />
        </>
    );
};

export default PropertySwitcher;
